import {
  Component, Inject, OnInit,
  ElementRef, ViewChild
} from '@angular/core';
 
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { SharedService } from '../../../../shared/services/shared.service';
import { PdfExportService } from '../../../../shared/services/pdf-export.service';
import { PdfViewerDialogComponent } from '../../../popups/processing/processing.component';
import { Router } from '@angular/router';
import { LoaderService } from '../../../../shared/services/loader.service';
import { DocumentCategory, DocumentSubCategory, DocumentSubCategoryName } from '../../../../shared/constants/document.constants';
import { DentalCredentialsService } from '../../../wizard/services/dental-credentials.service';
import { v4 as uuidv4 } from 'uuid';
 
@Component({
  selector: 'app-preview-modal',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './preview-model.component.html',
  styleUrls: ['./preview-model.component.scss']
})
export class PreviewModalComponent implements OnInit {
 
  @ViewChild('pdfWrapper', { static: false })
  pdfWrapper!: ElementRef<HTMLElement>;
  attestationContent: string = '';
  loading = true;
  mergedData: any = null;
  documentsByCategory: Record<string, any[]> = {};
  dentistInfoData: any = {};
  isCallFromFinish = false
  isError = false
  // ✅ Question Map
  private readonly QUESTION_MAP: Record<string, string> = {
    hasWeekendHours: "Does your practice have weekend hours?",
    hasEarlyMorningHours: "Does your practice have morning hours before 8 am?",
    hasLateEveningHours: "Does your practice have evening hours after 5 pm?",
    hasEmergencyPhoneNumber: "Do you have an emergency out-of-office phone number that is different than your main number?",
    hasPublicTransitNearby: "Is there public transit accessibility near your practice?",
    hasDisabledAdultCare: "Do you provide care to disabled adults?",
    hasDisabledChildrenCare: "Do you provide care to disabled children?",
    nonEnglishLanguagesSpoken: "Other than English, what other languages are spoken?",
    webSiteAddress: "What is your practice's full website address?",
    emergencyPhoneNumber: "Emergency Phone Number"
  };
  private readonly WORK_HISTORY_PLACEHOLDERS = new Set([
    'unknown',
    'un',
    '00000',
    'n/a',
    'na',
    'null',
    'undefined'
  ]);
  hasReadToBottom = false;

  constructor(
    private dialogRef: MatDialogRef<PreviewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sharedService: SharedService,
    private pdfExport: PdfExportService,public dialog: MatDialog,private router: Router,private loaderService:LoaderService,
    private dentalCredentialingService: DentalCredentialsService
  ) { }
 
  ngOnInit() {
    this.loadData();
    if(this.data && this.data.isCallfrom == "finish"){
      this.isCallFromFinish = true
    }   
    this.attestationContent =this.sharedService.credentialing_acceptattestation || localStorage.getItem("credentialing_acceptattestation") || '';
    }
 
  close() {
    this.dialogRef.close();
  }
 
  async downloadPdf() {
    const el = this.pdfWrapper?.nativeElement;
    if (!el) return;
 
    const dialogContent = el.querySelector('mat-dialog-content') as HTMLElement;
 
    let originalOverflow = '';
    let originalMaxHeight = '';
 
    if (dialogContent) {
      originalOverflow = dialogContent.style.overflow;
      originalMaxHeight = dialogContent.style.maxHeight;
 
      dialogContent.style.overflow = 'visible';
      dialogContent.style.maxHeight = 'none';
    }
 
    await new Promise(r => setTimeout(r, 300));
 
    await this.pdfExport.downloadElement(el, {
      filename: 'Credentialing-Preview.pdf',
      orientation: 'p',
      marginMm: 8,
      scale: 2
    });
 
    if (dialogContent) {
      dialogContent.style.overflow = originalOverflow;
      dialogContent.style.maxHeight = originalMaxHeight;
    }
  }

  private loadData() {
    let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
    const userId = localStorage.getItem('user_id');
    
    this.sharedService.getCredentiallingData({
      npi: credentialPayload['npi'],
      licenseNumber: credentialPayload['licenseNumber'],
      userId
    }).subscribe({
      next: (res: any) => {
        this.prepareFromApi(res);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      }
    });
  }
 
  private prepareFromApi(api: any) {
    if (!api) {
      this.mergedData = null;
      return;
    }
 
    const userName = localStorage.getItem("username") ?? '';
    const [firstName, ...rest] = userName.split(' ');
    const lastName = rest.join(' ');
    // ✅ Demographics
    const demographics = {
      ...(api?.demographics ?? {}),
      firstName: api?.demographics?.firstName ?? firstName,
      lastName: api?.demographics?.lastName ?? lastName
    };
 
    try {
      const specialities: Speciality[] = JSON.parse(localStorage.getItem('speciality')!);
      const result = specialities.find(item => item.value === demographics.specialty);
      demographics.speciality = result?.viewValue || demographics.specialty;
    } catch (error) {
    }
 
    // ✅ Merge locations with locationDirectories
    const locationDirectoryMap = new Map(
      (api?.locationDirectories ?? []).map((ld: any) => [ld.locationId, ld])
    );
 
    const locations = (api?.locations ?? []).map((loc: any) => {
      const isPrimaryLocation = this.isPrimaryLocation(loc?.isPrimary);
      const directory: any = isPrimaryLocation ? locationDirectoryMap.get(loc.locationId) : null;

      if (!directory) {
        return { ...loc, isPrimary: isPrimaryLocation, questions: [] };
      }

      const questions = Object.keys(this.QUESTION_MAP)
        .filter((key: string) => {
          if (key === 'emergencyPhoneNumber') {
            return directory.hasEmergencyPhoneNumber === true;
          }
          return key !== 'emergencyPhoneNumber';
        })
        .map((key: string) => ({
          key,
          question: this.QUESTION_MAP[key],
          answer: this.formatAnswer(directory[key])
        }))
        .filter((q: any) => q.answer !== 'N/A');

      return {
        ...loc,
        isPrimary: isPrimaryLocation,
        locationDirectories: directory,
        questions
      };
    });
 
    // ✅ Attestation mapping
    const attestationQA = (api?.attestationQuestions ?? []).map((q: any) => {
      const answer = (api?.attestationResponses ?? []).find(
        (r: any) => r.attestationQuestionId === q.attestionQuestionId
      );
      return {
        question: q.attestionQuestion,
        response: answer?.response ?? null,
        explanation: answer?.explanation ?? null
      };
    });
 
    let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
    const loggedUserDetails = JSON.parse(localStorage.getItem("loggedUserDetails") || '{}');
    const apiWorkHistory = Array.isArray(api?.workHistories) ? api.workHistories : [];
    const parseStoredWorkHistory = (key: string): any[] => {
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    const localWorkHistory = this.normalizeWorkHistoryRows(parseStoredWorkHistory('workhistories'));
    const warehouseWorkHistory = this.normalizeWorkHistoryRows(
      parseStoredWorkHistory('warehouseworkhistories').map((item: any) => ({
        ...item,
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        startDate: null
      }))
    );
    const workHistory = apiWorkHistory.length
      ? this.normalizeWorkHistoryRows(apiWorkHistory)
      : (localWorkHistory.length ? localWorkHistory : warehouseWorkHistory);
 
    // ✅ Final merged model
    this.mergedData = {
      dentistInfo: {
        npi: credentialPayload?.npi ?? null,
        licenseNumber: credentialPayload?.licenseNumber ?? null,
        licenseState: loggedUserDetails?.licenseState ?? null
      },
      demographics,
      credentials: api?.credentials ?? [],
      locations,
      workHistory: [...workHistory],
      attestation: api?.attestation ?? null,
      attestationQA
    };
  }
 
  // ✅ Answer formatter
  private formatAnswer(value: any): string {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value === null || value === undefined || value === '') return 'N/A';
    return value;
  }

  private toTrimmedString(value: any): string {
    return `${value ?? ''}`.trim();
  }
  
  private normalizeWorkHistoryDateValue(value: any): any {
    const normalized = this.toTrimmedString(value);
    if (!normalized || normalized === '0') {
      return null;
    }
    if (/^\d{8}$/.test(normalized)) {
      return `${normalized.substring(0, 4)}-${normalized.substring(4, 6)}-${normalized.substring(6, 8)}`;
    }
    return value;
  }
  
  private normalizeWorkHistoryRows(rows: any[]): any[] {
    return rows.map((item: any) => ({
      ...item,
      startDate: this.normalizeWorkHistoryDateValue(item?.startDate),
      endDate: this.normalizeWorkHistoryDateValue(item?.endDate)
    }));
  }

  isGapWorkHistory(organizationName: any): boolean {
    return this.toTrimmedString(organizationName).toLowerCase() === 'gap in work history';
  }

  displayWorkHistoryText(value: any): string {
    const normalized = this.toTrimmedString(value);
    if (!normalized) {
      return 'NA';
    }
    return this.WORK_HISTORY_PLACEHOLDERS.has(normalized.toLowerCase()) ? 'NA' : normalized;
  }

  displayWorkHistoryAddress(address1: any, address2: any): string {
    const line1 = this.displayWorkHistoryText(address1);
    const line2 = this.displayWorkHistoryText(address2);
    const parts = [line1, line2].filter((line) => line !== 'NA');
    return parts.length ? parts.join(', ') : 'NA';
  }

  private getBooleanValue(value: any): boolean | null {
    if (value === true || value === 'yes'  || value === 'true'  || value === '1') {
      return true;
    }
    if (value === false || value === 'no'  || value === 'false'  || value === '0') {
      return false;
    }
    return null;
  }

  getIsPracticingLabel(location: any): 'Yes' | 'No' {
    const stillPracticing = this.getBooleanValue(location?.stillPracticing);
    const endedPracticingAt = location?.endedPracticingAt ?? location?.endDate ?? location?.endedDate;
    // Show Still Practicing 'No' only for termed locations with an end date.
    if (stillPracticing === false && endedPracticingAt !== null && endedPracticingAt !== undefined && `${endedPracticingAt}`.trim() !== '') {
      return 'No';
    }
    return 'Yes';
  }

  shouldShowEndedPracticingAt(location: any): boolean {
    const stillPracticing = this.getBooleanValue(location?.stillPracticing);
    const endedPracticingAt = location?.endedPracticingAt ?? location?.endDate ?? location?.endedDate;
    return stillPracticing === false
      && endedPracticingAt !== null
      && endedPracticingAt !== undefined
      && `${endedPracticingAt}`.trim() !== '';
  }

  private isPrimaryLocation(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }
    const normalizedValue = `${value ?? ''}`.trim().toLowerCase();
    return normalizedValue === 'yes' || normalizedValue === 'true' || normalizedValue === '1';
  }

  agreed(){
    this.loaderService.show()
    this.attestUserAuditlog()
    this.submitCredentialApplication()
  }
  submitCredentialApplication() {
    const payload = {
        credentialingEventId: localStorage.getItem("credentialingEventId") ? localStorage.getItem("credentialingEventId") : 1
    };
    let popupData = {}
    this.sharedService.submitCredentialApplication(payload).subscribe({
        next:async (response) => {
            let Message = "Thank you for your application! You may receive an email from Overjet -1-617-812-4425 if additional information is required. You may log in and check you application status anytime."
            try {
              await this.saveApplication(payload?.credentialingEventId);
            } catch (e) {
            } finally {
              this.loaderService.hide()
              popupData = {
                "message" : Message,
                "showmsg" : true,
                "label" : "Ok",
                "isError" : false
              }
              this.isError = false
              this.dialogRef.close();
              this.openFinalPopup(popupData)
            }
        },
        error: (err) => {
            let errorMessage = 'An error had occurred, please try again. If it continues, please contact Provider Relations.';
            this.loaderService.hide()
            popupData = {
              "message" : errorMessage,
              "showmsg" : true,
              "label" : "Ok",
              "isError" : true
            }
            this.isError = true
            this.dialogRef.close();
            this.openFinalPopup(popupData)
            this.sharedService.logError("APIFailed", "FileConfirmationPopupComponent - submitCredentialApplication, Method:submitCredentialApplication", this.router.url.split('?')[0], err).subscribe();
        }
    });
  }
  attestUserAuditlog(){
    let auditLog:any={};
    auditLog["websiteCodeId"] = 50;
    auditLog["trailType"]='Trail';
    auditLog["trailWebSessionId"]=localStorage.getItem("trailWebSessionId")?.toUpperCase();
    auditLog["absolutePath"]=this.router.url;
    let userId = parseInt(localStorage.getItem("loggedUserId")!);
    auditLog["userId"] = userId ? userId : 0
    auditLog["impersonateId"]=0;
    auditLog["notes"]= "Attestation: Confirmed"
    this.sharedService.logWebTraillog(auditLog).subscribe(data=>{
    });
  }
  openFinalPopup(popupData:any){
    this.dialog.open(PdfViewerDialogComponent, {
      disableClose : true,
      data : popupData
     });
  }
  onScroll(el: HTMLElement): void {
    // If the user has already scrolled to the bottom once, don't keep calculating
    if (this.hasReadToBottom) return;
 
    // Logic: Current Scroll + Visible Area >= Total Content Height
    const threshold = 5; 
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
 
    if (isAtBottom) {
      this.hasReadToBottom = true;
    }
  }

  async saveApplication(credentialingEventId:any) {
    let el: any = this.pdfWrapper?.nativeElement;
    if (!el) return;

    const dialogContent = el.querySelector('mat-dialog-content') as HTMLElement;

    let originalOverflow = '';
    let originalMaxHeight = '';


    if (dialogContent) {
      originalOverflow = dialogContent.style.overflow;
      originalMaxHeight = dialogContent.style.maxHeight;
      dialogContent.style.overflow = 'visible';
      dialogContent.style.maxHeight = 'none';
    }

    await new Promise(r => setTimeout(r, 300));

    try {
      // let finalData: any = el.outerHTML;
      let stylesData: any = Array.from(document.styleSheets).map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('');
        } catch (e) {
          return '';
        }
      }).join('');
      if (stylesData) {
        stylesData = stylesData.replace(/\.\.\//g, `${window.location.origin}/assets/`).replace(/@font-face/g, '.no-use');
        stylesData = `${stylesData}
        body {
          font-family: Gotham-Book;
        }
        .mainClass {
          padding: 24px !important;
          background-color: white !important;
        }
        div#main-content{
          padding:20px !important
        }
        .grid-container{
          border:0px !important;
        }
        .preview-dialog .mat-dialog-container {
          border-radius: 10px;
          padding: 24px;
          background: #f8f9fb;
        }
        section {
          margin-top: 20px;
          padding: 18px 20px;
          background: #ffffff;
          border-radius: 8px;
          border: 1px solid #e6e9ef;
        }
        section h3 {
          font-size: 16px !important;
          // font-weight: 600;
          color: #333;
          margin-bottom: 14px;
          border-bottom: 1px solid #edf0f5;
          padding-bottom: 6px;
        }
  
        section p {
          margin: 6px 0;
          font-size: 14px !important;
          color: #333;
        }
  
        section p strong {
          display: inline-block;
          width: 140px;
          color: #495057;
          // font-weight: 600 !important;
        }
        .authorization-title{
          font-family: Gotham-Medium, sans-serif !important;
        }
        .attestinfo{
          font-size: 14px;
          line-height: 1.6 !important;
        }
        .attestHeading{
          text-align: center !important;
          //font-size: 16px !important;
 
        }
        .attestHead{
          color: #262626;
        }
        .preview-container{
          padding: 10px;
        }
 
        .demo-section {
          margin-bottom: 16px;
        }
 
        .two-col {
          display: grid;
          grid-template-columns: 180px 1fr;
          row-gap: 4px;
          column-gap: 4px;
          //font-size: 14px !important;
        }
 
        .two-col div {
          align-self: center;
          //font-size: 14px !important;
        }
 
        .location-card {
          margin-bottom: 20px;
        }
       
        .location-questions {
          margin-top: 12px;
          margin-left:10px;
        }
 
        .question-row {
          display: grid;
          grid-template-columns: 70% 30%;
          padding: 6px 0;
          border-bottom: 1px solid #eee;
        }
       
        .question-text {
          padding-right: 10px;
          //font-size: 11px !important;
          // font-weight: 200 !important;
        }
       
        .answer-text {
          text-align: right;
          // font-weight: 200 !important;
          //font-size: 11px !important;
        }
 
        .sub-heading {
          margin: 10px 0;
          //font-size: 14px !important;
        }
       
        .full-width {
          grid-column: 1 / -1;
        }
        
        .submitted-date {
          position: absolute !important;
          right: 24 !important;
          top: 80 !important;
        }
      `
      }
      const now = new Date();
      const submittedDate = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
      
      const submittedHTML = `<span class="submitted-date">Submitted Date: ${submittedDate}</span>`
      let finalData: any = `<style>${stylesData}</style>${submittedHTML}${el.outerHTML}`;
      const safeHtml = finalData?.replace(/"/g, "'");

      const apiCall = this.dentalCredentialingService.doownloadPreLoginPdf({ html: safeHtml, isMemberInES: false, pdfSize: "A3" });
      apiCall.subscribe({
        next: async (providerResponse: any) => {
          const blob = new Blob([providerResponse], { type: 'application/pdf' });
          const file = new File([blob], `Credentialing-Preview-${Date.now()}.pdf`, {
            type: 'application/pdf'
          });
          let date = new Date();

          let year = date.getFullYear();
          let month = String(date.getMonth() + 1).padStart(2, '0');
          let day = String(date.getDate()).padStart(2, '0');
          const uniqueId = uuidv4();
          const timestamp = date.getTime();
          let credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
          const fileName: any = `${year}${month}${day}_${timestamp}_${credentialPayload['npi']}_${uniqueId}`;
          const response: any = await this.saveDocument(file, fileName,credentialingEventId);
          const documents = JSON.parse(localStorage.getItem('documents') || '[]');

          const payload = {
            documentId: response?.documentId,
            category: 'SignedDocument',
            categoryId: DocumentCategory.SignedDocument,
            credentialingEventId: localStorage.getItem('credentialingEventId'),
            fileName: fileName,
            isSigned: true,
            subCategoryId: DocumentSubCategory.CredentialingApplication,
            subcategory: DocumentSubCategoryName[DocumentSubCategory.CredentialingApplication]
          };
          documents.push(payload);

          this.loaderService.hide();
        },
        error: (err: any) => {
          this.sharedService.logError("APIFailed", "PreviewModel - GeneratePDF, Method:saveApplication", this.router.url.split('?')[0], err).subscribe();
          this.loaderService.hide();
        }
      });

    } catch (err) {

    }
    if (dialogContent) {
      dialogContent.style.overflow = originalOverflow;
      dialogContent.style.maxHeight = originalMaxHeight;
    }
  }

  async saveDocument(file: any, filename: any,EventId?:any) {
    try {
      const loginUserId = localStorage.getItem("loggedUserId");
      const sub_category = DocumentSubCategory.CredentialingApplication;
      const credentialingEventId = localStorage.getItem('credentialingEventId') ? localStorage.getItem('credentialingEventId') : EventId;
  
      let params = {
        "credentialingEventId": credentialingEventId,
        "categoryId": DocumentCategory.SignedDocument,
        "subCategoryId": sub_category,
        "isSigned": true,
        "fileName": filename,
        "userId": loginUserId || -1
      }
      return await this.dentalCredentialingService.saveDocument(file, params);
    } catch (error: any) {
      this.sharedService.logError("APIFailed", "PreviewModel - saveDocument, Method:saveDocument", this.router.url.split('?')[0], error).subscribe();
    }  
  }

}
 
interface Speciality {
  value: number;
  viewValue: string;
}